import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { PasswordChange } from '../../../types/user';

interface PasswordChangeFormProps {
  onSubmit: (data: PasswordChange) => void;
  isLoading: boolean;
  error?: string | null;
  success?: boolean;
}

export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  onSubmit,
  isLoading,
  error,
  success,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<PasswordChange>();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const newPassword = watch('new_password');

  const handleFormSubmit = (data: PasswordChange) => {
    onSubmit(data);
    if (success) {
      reset();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        비밀번호 변경
      </Typography>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={3}>
          <Controller
            name="current_password"
            control={control}
            rules={{ required: '현재 비밀번호를 입력해주세요.' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="현재 비밀번호"
                type={showCurrentPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.current_password}
                helperText={errors.current_password?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="new_password"
            control={control}
            rules={{
              required: '새 비밀번호를 입력해주세요.',
              minLength: {
                value: 8,
                message: '비밀번호는 최소 8자 이상이어야 합니다.',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="새 비밀번호"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.new_password}
                helperText={errors.new_password?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="new_password_confirm"
            control={control}
            rules={{
              required: '비밀번호 확인을 입력해주세요.',
              validate: (value) =>
                value === newPassword || '새 비밀번호와 일치하지 않습니다.',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="새 비밀번호 확인"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                error={!!errors.new_password_confirm}
                helperText={errors.new_password_confirm?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Box sx={{ minHeight: 48 }}>
            {error && (
              <Fade in>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Fade>
            )}
            {success && (
              <Fade in>
                <Alert severity="success" sx={{ mb: 2 }}>
                  비밀번호가 성공적으로 변경되었습니다.
                </Alert>
              </Fade>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LockReset />}
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 2,
            }}
          >
            {isLoading ? '변경 중...' : '변경'}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};
